import React, { useEffect, useState } from "react";
import { getCoinTransactions } from "../../services/walletService";
import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  ArrowUpRight,
  ArrowDownLeft,
} from "lucide-react";

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("");

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const data = await getCoinTransactions(page, 10, filter);
      setTransactions(data.result.transactions);
      setTotalPages(data.result.pagination.totalPagesCount);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, filter]);

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setPage(1);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-white font-bold text-lg">Coin Transactions</h3>
        <div className="relative">
          <Filter
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
          />
          <select
            value={filter}
            onChange={handleFilterChange}
            className="appearance-none bg-[#2A1B3D] border border-white/10 text-gray-300 text-sm rounded-xl pl-9 pr-8 py-2.5 focus:ring-purple-500 focus:border-purple-500 outline-none cursor-pointer"
          >
            <option value="">All Transactions</option>
            <option value="recharge">Recharge</option>
            <option value="spent_contest">Contest Entry</option>
            <option value="earned_contest">Contest Winnings</option>
            <option value="spent_plan">Plan Purchase</option>
            <option value="earned_question">Quiz Earned</option>
            <option value="spent_question">Quiz Deducted</option>
            <option value="withdrawn">Withdrawal</option>
            <option value="refund">Refund</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : transactions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <ArrowDownLeft size={40} className="mb-3 opacity-30" />
          <p className="text-sm">No transactions found.</p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {transactions.map((txn) => (
              <div
                key={txn._id}
                className="bg-[#2A1B3D]/50 border border-white/5 rounded-xl p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        txn.amount > 0 ? "bg-green-500/10" : "bg-red-500/10"
                      }`}
                    >
                      {txn.amount > 0 ? (
                        <ArrowDownLeft size={14} className="text-green-400" />
                      ) : (
                        <ArrowUpRight size={14} className="text-red-400" />
                      )}
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        txn.amount > 0
                          ? "bg-green-500/10 text-green-400"
                          : "bg-red-500/10 text-red-400"
                      }`}
                    >
                      {txn.type.replace("_", " ")}
                    </span>
                  </div>
                  <span
                    className={`text-sm font-bold ${
                      txn.amount > 0 ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {txn.amount > 0 ? "+" : ""}
                    {txn.amount}
                  </span>
                </div>
                <p className="text-gray-400 text-xs mb-1">{txn.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-xs">
                    {format(new Date(txn.createdOn), "dd MMM yyyy, hh:mm a")}
                  </span>
                  <span className="text-gray-500 text-xs">
                    Bal: {txn.balanceAfter}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Date
                  </th>
                  <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Description
                  </th>
                  <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Type
                  </th>
                  <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Amount
                  </th>
                  <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider py-3 px-4">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) => (
                  <tr
                    key={txn._id}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="py-3 px-4 text-gray-400 text-sm">
                      {format(new Date(txn.createdOn), "dd MMM yyyy, hh:mm a")}
                    </td>
                    <td className="py-3 px-4 text-gray-300 text-sm">
                      {txn.description}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                          txn.amount > 0
                            ? "bg-green-500/10 text-green-400"
                            : "bg-red-500/10 text-red-400"
                        }`}
                      >
                        {txn.type.replace("_", " ")}
                      </span>
                    </td>
                    <td
                      className={`py-3 px-4 text-right text-sm font-bold ${
                        txn.amount > 0 ? "text-green-400" : "text-red-400"
                      }`}
                    >
                      {txn.amount > 0 ? "+" : ""}
                      {txn.amount}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-400 text-sm">
                      {txn.balanceAfter}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg bg-[#2A1B3D] border border-white/10 text-gray-400 hover:text-white hover:border-purple-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-gray-400 text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg bg-[#2A1B3D] border border-white/10 text-gray-400 hover:text-white hover:border-purple-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
